import { API_BASE_URL } from "@/lib/constants/app";
import { getSession } from "@/lib/supabase";
import { handleSessionExpiredError } from "@/lib/utils/auth";

/**
 * BackendProviderService - Service layer for communicating with backend AI providers via SSE
 * This replaces direct API calls to AI providers with calls to our backend
 */
class BackendProviderService {
  constructor(options = {}) {
    this.baseUrl = options.baseUrl || API_BASE_URL;
    this.defaultProvider = options.defaultProvider || "openai";
    this.defaultModel = options.defaultModel || "gpt-4.1-mini";
  }

  /**
   * Create a streaming chat completion via backend SSE
   * @param {Object} params - Parameters for the completion
   * @param {function} onUpdate - Callback function for each chunk of the stream
   * @param {Object} options - Additional options like AbortController signal
   * @returns {Promise<void>} - Promise that resolves when the stream is complete
   */
  async createChatCompletionStream(params, onUpdate, options = {}) {
    const {
      provider = this.defaultProvider,
      model = this.defaultModel,
      messages,
      temperature,
      max_tokens,
      tools,
      tool_choice,
      instructions,
      contextData = {},
      ...otherParams
    } = params;

    // Validate required parameters
    if (!messages || !Array.isArray(messages)) {
      throw new Error("Messages array is required");
    }

    // CRITICAL FIX: Extract system instructions from messages array
    // The follow-up stream adds system instructions as the first message
    let systemInstructions = instructions;
    let processedMessages = [...messages];

    // Check if first message is a system message with instructions
    const hasSystemMessage =
      messages.length > 0 && messages[0].role === "system";

    if (hasSystemMessage) {
      const systemMessage = messages[0];
      if (systemMessage.content && systemMessage.content.trim()) {
        // Use the system message content as instructions
        systemInstructions = systemMessage.content;
      }
    } else {
      // CRITICAL FIX: If no system message but we have instructions, add system message
      if (systemInstructions && systemInstructions.trim()) {
        // Add system message at the beginning
        processedMessages = [
          {
            role: "system",
            content: systemInstructions,
          },
          ...processedMessages,
        ];
      }
    }

    // ADDITIONAL SAFEGUARD: Log final state before sending to backend

    // Get user ID from session for token tracking
    let userId = null;
    try {
      const session = await getSession();
      userId = session?.user?.id || null;
    } catch (error) {
      // Silently fail - token tracking will work without user ID
    }

    // Get threadId from params or otherParams (may be passed as threadId or threadIdForStream)
    const threadId =
      params.threadId ||
      params.threadIdForStream ||
      otherParams.threadId ||
      otherParams.threadIdForStream ||
      null;

    // Prepare request body
    const requestBody = {
      provider,
      model,
      messages: processedMessages,
      temperature,
      max_tokens,
      tools,
      tool_choice,
      instructions: systemInstructions, // Use extracted system instructions
      contextData,
      tracking: userId || threadId ? { userId, threadId } : undefined, // Include user ID and thread ID for token tracking
      ...otherParams,
    };

    // Log enabled tools for debugging tool validation issues
    const toolNames =
      tools?.map((t) => t.function?.name || t.name).filter(Boolean) || [];
    console.log("[BackendProvider] Request:", {
      provider,
      model,
      toolCount: tools?.length || 0,
      enabledTools: toolNames,
      tool_choice,
      hasExplicitToolSelection: contextData?.hasExplicitToolSelection,
    });

    // DEBUG: Log the exact prompt (all messages) being sent to the backend API
    console.log("📤 [DEBUG][PROMPT] Full Prompt Being Sent to Backend API:", {
      provider,
      model,
      threadId: threadId || "N/A",
      userId: userId || "N/A",
      messageCount: processedMessages.length,
      temperature,
      max_tokens,
      instructions: systemInstructions
        ? `[${systemInstructions.length} chars]`
        : "N/A",
      tools: toolNames,
      tool_choice,
      fullMessages: processedMessages.map((msg, idx) => ({
        index: idx,
        role: msg.role,
        content:
          typeof msg.content === "string"
            ? msg.content
            : Array.isArray(msg.content)
              ? msg.content.map((c) => ({
                  type: c.type,
                  text: c.type === "text" ? c.text : "[non-text content]",
                  image_url:
                    c.type === "image_url" ? "[image data]" : undefined,
                }))
              : "[complex content]",
        tool_calls: msg.tool_calls
          ? `[${msg.tool_calls.length} tool call(s)]`
          : undefined,
        tool_call_id: msg.tool_call_id,
        name: msg.name,
      })),
    });

    // Extract AbortController signal from options if present
    const signal = options?.signal;

    try {
      // Check if already aborted before starting
      if (signal?.aborted) {
        throw new DOMException("Stream aborted before starting", "AbortError");
      }

      // Make request to backend SSE endpoint
      const response = await fetch(`${this.baseUrl}/api/ai/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        signal, // Pass the abort signal to fetch
      });

      if (!response.ok) {
        // Handle 401 Unauthorized - session expired
        if (handleSessionExpiredError(response, "BackendProvider")) {
          throw new Error('Session expired');
        }

        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `Backend AI API Error (${response.status}): ${errorData.error || response.statusText}`
        );
      }

      // Process the SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let sessionId = null;
      let chunkCount = 0;
      let streamStartTime = Date.now();
      let lastChunkTime = Date.now();

      // Add timeout warning for slow responses
      const timeoutWarning = setTimeout(() => {
        if (chunkCount === 0) {
          // Timeout warning removed
        }
      }, 10000);

      try {
        while (true) {
          // Check for abort signal before reading the next chunk
          if (signal?.aborted) {
            // Try to abort the session on the backend
            if (sessionId) {
              try {
                await fetch(`${this.baseUrl}/api/ai/chat/abort/${sessionId}`, {
                  method: "POST",
                });
              } catch (abortError) {
                // Abort error handling removed
              }
            }

            throw new DOMException("Stream aborted by user", "AbortError");
          }

          const { done, value } = await reader.read();
          if (done) {
            break;
          }

          // Decode the chunk and add it to the buffer
          buffer += decoder.decode(value, { stream: true });

          // Process complete messages from the buffer
          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // Keep the last incomplete line in the buffer

          for (const line of lines) {
            if (line.trim() === "") continue; // Skip empty lines

            if (line.startsWith("data: ")) {
              try {
                const jsonStr = line.slice(6); // Remove "data: " prefix
                const data = JSON.parse(jsonStr);

                // Handle different event types
                switch (data.type) {
                  case "connection":
                    sessionId = data.sessionId;
                    break;

                  case "chunk": {
                    chunkCount++;
                    lastChunkTime = Date.now();

                    // Clear timeout warning on first chunk
                    if (chunkCount === 1) {
                      clearTimeout(timeoutWarning);
                    }

                    // Check abort signal before processing chunk
                    if (signal?.aborted) {
                      throw new DOMException(
                        "Stream aborted during chunk processing",
                        "AbortError"
                      );
                    }

                    // Forward the chunk to the original onUpdate callback
                    if (data.chunk) {
                      // ADDED: Ensure model name is available in chunk for Grok detection
                      if (!data.chunk.model && model) {
                        data.chunk.model = model;
                      }

                      // ADDED: Enhanced DeepSeek reasoning transition detection
                      const hasReasoningContent =
                        data.chunk.choices?.[0]?.delta?.reasoning_content;
                      const hasRegularContent =
                        data.chunk.choices?.[0]?.delta?.content;
                      const isDeepSeekReasoner =
                        provider === "deepseek" &&
                        (model.includes("reasoner") || model.includes("r1"));

                      // Log reasoning to regular content transition for DeepSeek
                      if (isDeepSeekReasoner) {
                        if (hasRegularContent && !hasReasoningContent) {
                          // CRITICAL FIX: Add reasoning completion metadata when transitioning to regular content
                          if (!data.chunk.choices[0].delta.metadata) {
                            data.chunk.choices[0].delta.metadata = {};
                          }
                          data.chunk.choices[0].delta.metadata.reasoning_transition = true;
                          data.chunk.choices[0].delta.metadata.reasoning_complete = true;
                        }
                      }

                      onUpdate(data.chunk);
                    }
                    break;
                  }

                  case "reasoning_complete": {
                    // Create a special chunk to signal reasoning completion
                    const reasoningCompleteChunk = {
                      choices: [
                        {
                          delta: {
                            metadata: {
                              reasoning_complete: true,
                              reasoning_seconds: data.duration,
                              reasoning_token_count: data.tokenCount,
                            },
                          },
                          finish_reason: null,
                        },
                      ],
                    };

                    onUpdate(reasoningCompleteChunk);
                    break;
                  }

                  case "complete": {
                    clearTimeout(timeoutWarning);

                    // ADDED: Capture citations from Perplexity responses
                    const citations = data.citations || null;

                    // Debug log for citations
                    console.log(
                      "[BackendProviderService] Stream complete event:",
                      {
                        hasCitations: !!citations,
                        citationsCount: citations?.length || 0,
                        citationsSample: citations?.slice(0, 2),
                        dataKeys: Object.keys(data || {}),
                        provider: provider,
                      }
                    );

                    // If citations are present, create a final chunk with citations metadata
                    if (
                      citations &&
                      Array.isArray(citations) &&
                      citations.length > 0
                    ) {
                      console.log(
                        "[BackendProviderService] Sending citations chunk with",
                        citations.length,
                        "sources"
                      );
                      const citationsChunk = {
                        choices: [
                          {
                            delta: {
                              metadata: {
                                citations: citations,
                                hasCitations: true,
                              },
                            },
                            finish_reason: null,
                          },
                        ],
                      };
                      onUpdate(citationsChunk);
                    }

                    // ADDED: Dispatch custom event when streaming completes
                    try {
                      const streamCompleteEvent = new CustomEvent(
                        "aiStreamComplete",
                        {
                          detail: {
                            provider: provider,
                            model: model,
                            sessionId: sessionId,
                            chunkCount: chunkCount,
                            duration: Date.now() - streamStartTime,
                            citations: citations, // Include citations in event
                          },
                        }
                      );
                      window.dispatchEvent(streamCompleteEvent);
                    } catch (eventError) {
                      console.warn(
                        "Could not dispatch stream complete event:",
                        eventError
                      );
                    }

                    return; // Stream completed successfully
                  }

                  case "error": {
                    clearTimeout(timeoutWarning);
                    // Create a specific error that will be re-thrown
                    const streamError = new Error(data.error || "Unknown stream error");
                    streamError.isStreamError = true;
                    streamError.retryable = data.retryable ?? true; // Pass through backend's retryable flag
                    throw streamError;
                  }

                  default:
                    // Unknown event type
                    break;
                }
              } catch (e) {
                if (e.name === "AbortError") {
                  throw e; // Re-throw abort errors
                }
                if (e.isStreamError) {
                  throw e; // Re-throw stream errors from backend
                }
                // Only swallow JSON parsing errors, not stream errors
                console.warn("Failed to parse SSE message:", e.message);
              }
            }
          }
        }
      } catch (streamError) {
        // Check if this is an abort error
        if (streamError.name === "AbortError") {
          // Try to abort the session on the backend
          if (sessionId) {
            try {
              await fetch(`${this.baseUrl}/api/ai/chat/abort/${sessionId}`, {
                method: "POST",
              });
            } catch (abortError) {
              // Failed to abort backend session
            }
          }
          throw streamError; // Rethrow to be caught by the outer catch
        }
        throw streamError;
      } finally {
        // Always release the reader when done or on error
        reader.releaseLock();
      }
    } catch (error) {
      // Check if this is an abort error from the AbortController
      if (error.name === "AbortError") {
        // Confirmed abort error
      }
      throw error;
    }
  }

  /**
   * Create a non-streaming chat completion via backend
   * @param {Object} params - Parameters for the completion
   * @returns {Promise<Object>} - Promise resolving to completion response
   */
  async createChatCompletion(params) {
    const {
      provider = this.defaultProvider,
      model = this.defaultModel,
      messages,
      temperature,
      max_tokens,
      tools,
      tool_choice,
      instructions,
      ...otherParams
    } = params;

    // Validate required parameters
    if (!messages || !Array.isArray(messages)) {
      throw new Error("Messages array is required");
    }

    // CRITICAL FIX: Extract system instructions from messages array
    let systemInstructions = instructions;
    let processedMessages = [...messages];

    // Check if first message is a system message with instructions
    const hasSystemMessage =
      messages.length > 0 && messages[0].role === "system";

    if (hasSystemMessage) {
      const systemMessage = messages[0];
      if (systemMessage.content && systemMessage.content.trim()) {
        // Use the system message content as instructions
        systemInstructions = systemMessage.content;
      }
    } else {
      // CRITICAL FIX: If no system message but we have instructions, add system message
      if (systemInstructions && systemInstructions.trim()) {
        // Add system message at the beginning
        processedMessages = [
          {
            role: "system",
            content: systemInstructions,
          },
          ...processedMessages,
        ];
      }
    }

    // Get user ID from session for token tracking
    let userId = null;
    try {
      const session = await getSession();
      userId = session?.user?.id || null;
    } catch (error) {
      // Silently fail - token tracking will work without user ID
    }

    // Prepare request body
    const requestBody = {
      provider,
      model,
      messages: processedMessages,
      temperature,
      max_tokens,
      tools,
      tool_choice,
      instructions: systemInstructions, // Use extracted system instructions
      tracking: userId ? { userId } : undefined, // Include user ID for token tracking
      ...otherParams,
    };

    // DEBUG: Log the exact prompt (all messages) being sent to the backend API (non-streaming)
    const toolNames =
      tools?.map((t) => t.function?.name || t.name).filter(Boolean) || [];
    console.log(
      "📤 [DEBUG][PROMPT] Full Prompt Being Sent to Backend API (Non-Streaming):",
      {
        provider,
        model,
        userId: userId || "N/A",
        messageCount: processedMessages.length,
        temperature,
        max_tokens,
        instructions: systemInstructions
          ? `[${systemInstructions.length} chars]`
          : "N/A",
        tools: toolNames,
        tool_choice,
        fullMessages: processedMessages.map((msg, idx) => ({
          index: idx,
          role: msg.role,
          content:
            typeof msg.content === "string"
              ? msg.content
              : Array.isArray(msg.content)
                ? msg.content.map((c) => ({
                    type: c.type,
                    text: c.type === "text" ? c.text : "[non-text content]",
                    image_url:
                      c.type === "image_url" ? "[image data]" : undefined,
                  }))
                : "[complex content]",
          tool_calls: msg.tool_calls
            ? `[${msg.tool_calls.length} tool call(s)]`
            : undefined,
          tool_call_id: msg.tool_call_id,
          name: msg.name,
        })),
      }
    );

    const response = await fetch(`${this.baseUrl}/api/ai/chat/completion`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      // Handle 401 Unauthorized - session expired
      if (handleSessionExpiredError(response, "BackendProvider")) {
        throw new Error('Session expired');
      }

      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Backend AI API Error (${response.status}): ${errorData.error || response.statusText}`
      );
    }

    const data = await response.json();
    return data.result;
  }

  /**
   * List available models for a provider
   * @param {string} provider - Provider name
   * @returns {Promise<Array>} - Promise resolving to array of model objects
   */
  async listModels(provider = this.defaultProvider) {
    const response = await fetch(`${this.baseUrl}/api/ai/models/${provider}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Backend AI API Error (${response.status}): ${errorData.error || response.statusText}`
      );
    }

    const data = await response.json();
    return data.models || [];
  }

  /**
   * Get available providers
   * @returns {Promise<Array>} - Promise resolving to array of provider objects
   */
  async getProviders() {
    const response = await fetch(`${this.baseUrl}/api/ai/providers`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Backend AI API Error (${response.status}): ${errorData.error || response.statusText}`
      );
    }

    const data = await response.json();
    return data.providers || [];
  }

  /**
   * Check provider availability
   * @param {string} provider - Provider name
   * @returns {Promise<boolean>} - Promise resolving to availability status
   */
  async checkAvailability(provider = this.defaultProvider) {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/ai/providers/${provider}/status`
      );

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.available || false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Format message history (compatibility method)
   * @param {Array} messages - Array of message objects
   * @param {string} instructions - Assistant instructions
   * @returns {Array} - Formatted messages
   */
  formatMessageHistory(messages, instructions) {
    // Validate and fix tool call sequences for OpenAI compliance
    const formattedMessages = this.validateAndFixToolCallSequences(messages);

    // Add instructions as system message if provided and not already present
    if (instructions && !formattedMessages.some((m) => m.role === "system")) {
      formattedMessages.unshift({
        role: "system",
        content: instructions,
      });
    }

    return formattedMessages;
  }

  /**
   * Validate and fix tool call sequences to ensure OpenAI API compliance
   * @param {Array} messages - Array of message objects
   * @returns {Array} - Fixed messages array
   */
  validateAndFixToolCallSequences(messages) {
    if (!messages || !Array.isArray(messages)) {
      return [];
    }

    const fixedMessages = [];
    let i = 0;

    while (i < messages.length) {
      const message = messages[i];

      // Handle assistant messages with tool_calls
      if (
        message.role === "assistant" &&
        message.tool_calls &&
        Array.isArray(message.tool_calls) &&
        message.tool_calls.length > 0
      ) {
        // Add the assistant message
        fixedMessages.push({
          ...message,
          role: "assistant",
          content: message.content || "",
          tool_calls: message.tool_calls.map((tc) => ({
            id: tc.id,
            type: tc.type || "function",
            function: {
              name: tc.function?.name || "unknown_function",
              arguments: tc.function?.arguments || "{}",
            },
          })),
        });

        // Collect tool_call_ids that need responses
        const expectedToolCallIds = new Set(
          message.tool_calls.map((tc) => tc.id)
        );
        const foundToolResponses = new Set();

        // Look ahead for corresponding tool messages
        let j = i + 1;
        const toolMessages = [];

        while (j < messages.length && messages[j].role === "tool") {
          const toolMessage = messages[j];
          const toolCallId = toolMessage.tool_call_id;

          if (expectedToolCallIds.has(toolCallId)) {
            foundToolResponses.add(toolCallId);
            toolMessages.push({
              role: "tool",
              tool_call_id: toolCallId,
              content:
                typeof toolMessage.content === "string"
                  ? toolMessage.content
                  : JSON.stringify(toolMessage.content || "", null, 2),
            });
          }
          j++;
        }

        // Create missing tool responses for any tool_calls without responses
        const missingToolCallIds = [...expectedToolCallIds].filter(
          (id) => !foundToolResponses.has(id)
        );

        if (missingToolCallIds.length > 0) {
          for (const missingId of missingToolCallIds) {
            const originalToolCall = message.tool_calls.find(
              (tc) => tc.id === missingId
            );
            const toolName =
              originalToolCall?.function?.name || "unknown_function";

            toolMessages.push({
              role: "tool",
              tool_call_id: missingId,
              content: `Tool "${toolName}" executed successfully.`,
            });
          }
        }

        // Add all tool messages
        fixedMessages.push(...toolMessages);

        // Skip the tool messages we already processed
        i = j;
      }
      // Handle regular messages (user, system, assistant without tool_calls)
      else {
        // For assistant messages without tool_calls, keep as is
        if (message.role === "assistant") {
          fixedMessages.push({
            role: "assistant",
            content: message.content || "",
          });
        }
        // For user and system messages, keep as is
        else if (message.role === "user" || message.role === "system") {
          fixedMessages.push({
            role: message.role,
            content: message.content || "",
          });
        }
        // Skip orphaned tool messages (tool messages without preceding assistant tool_calls)
        else if (message.role === "tool") {
          // Skipping orphaned tool message
        }

        i++;
      }
    }

    return fixedMessages;
  }

  /**
   * Check if a model is a reasoning model (compatibility method)
   * @param {string} modelName - The model name to check
   * @returns {boolean} - Whether the model is a reasoning model
   */
  isReasoningModel(modelName) {
    // This logic is now handled by the backend providers
    const reasoningModels = [
      "o1",
      "o1-preview",
      "o1-mini",
      "o3",
      "o3-preview",
      "o3-mini",
      "deepseek-r1",
      "deepseek-reasoner",
      "sonar-reasoning",
    ];

    return reasoningModels.some(
      (model) => modelName === model || modelName.includes(model)
    );
  }
}

// Create singleton instance
const backendProviderService = new BackendProviderService();

export { BackendProviderService };
export default backendProviderService;
